import { FC, useState, useEffect } from 'react'
import logger from 'lib/logger'
import { clearApolloStore, onUserLoginWebviewMessage, setJwt } from 'lib/user'
import { getInputMaskPhoneNumber, removeInternationalCode } from 'lib/phone'
import useAuth from 'hooks/useAuth'
import { User } from 'graphql/types'
import { TUser } from '../types'
import Loader from 'components/Loader'
import FormElement from 'components/form/FormElement'
import Input from 'components/form/Input'
import Button from 'components/Button'
import Typography from 'components/Typography'
import styles from './Login.module.scss'

type TProps = {
  phone: string
  profile?: TUser
  onSuccess: (userProfile: User) => void
  setVisitStep: (step: number) => void
}

const Login: FC<TProps> = ({ phone, profile, onSuccess, setVisitStep }) => {
  const [inputCode, setInputCode] = useState('')
  const [step, setStep] = useState<
    'loading' | 'phoneError' | 'code' | 'codeError'
  >()

  const { createAuthenticationCode, verifyAuthenticationCode, isLoading } =
    useAuth()

  const submitPhone = async () => {
    setStep('loading')
    logger.action('listing-detail-schedulemodal-login-phone-send')

    try {
      const { data } = await createAuthenticationCode({
        variables: { phone },
      })

      if (data?.signInCreateAuthenticationCode?.enqueued === 'SUCCESS') {
        setStep('code')
        logger.action('listing-detail-schedulemodal-login-phone-send-success')
      } else {
        setStep('phoneError')
        logger.action('listing-detail-schedulemodal-login-phone-send-error')
      }
    } catch (e) {
      logger.capture(e)
      setStep('phoneError')
      logger.action('listing-detail-schedulemodal-login-phone-send-error')
    }
  }

  const submitVerifyCode = async () => {
    setStep('loading')
    logger.action('listing-detail-schedulemodal-login-code-send')

    try {
      const { data } = await verifyAuthenticationCode({
        variables: { phone, code: inputCode },
      })

      if (data?.signInVerifyAuthenticationCode?.jwt) {
        setJwt(data?.signInVerifyAuthenticationCode?.jwt)
        onUserLoginWebviewMessage(data?.signInVerifyAuthenticationCode?.jwt)
        void clearApolloStore()
        logger.action('listing-detail-schedulemodal-login-code-send-success')
      } else {
        setStep('codeError')
        logger.action('listing-detail-schedulemodal-login-code-send-error')
      }
    } catch (e) {
      logger.capture(e)
      setStep('codeError')
      logger.action('listing-detail-schedulemodal-login-code-send-error')
    }
  }

  useEffect(() => {
    if (profile?.id) {
      onSuccess(profile)
    }
  }, [profile])

  useEffect(() => {
    if (!step && step !== 'loading') {
      setStep('loading')
      void submitPhone()
    }
  }, [])

  if (step === 'loading' || !step || isLoading) return <Loader />

  if (step === 'phoneError')
    return (
      <div className={styles.ecVisitModalLogin}>
        <Typography>
          Erro ao enviar o <strong>código de login</strong>.<br />
          Por favor, tente novamente ou verifique os dados.
        </Typography>
        <Button theme="pink" onClick={() => void submitPhone()}>
          Tentar novamente
        </Button>
        <Button onClick={() => setVisitStep(2)}>Verificar os dados</Button>
      </div>
    )

  return (
    <form className={styles.ecVisitModalLogin}>
      <Typography>
        Digite o <strong>código de login</strong> enviado para o seu número de
        WhatsApp{' '}
        {phone && (
          <strong>
            {getInputMaskPhoneNumber(removeInternationalCode(phone))}
          </strong>
        )}
      </Typography>

      <FormElement
        status={'error'}
        helpText={{
          ariaDescribeId: 'code',
          value:
            step === 'codeError'
              ? 'Erro ao validar o código de login. Por favor, verifique o código e tente novamente.'
              : undefined,
        }}
      >
        <Input
          inputElementProps={{
            id: 'code',
            autoFocus: true,
            name: 'code',
            type: 'number',
            tabIndex: 0,
            placeholder: '____',
            value: inputCode,
            onChange: (e) => {
              setInputCode(e.target.value.slice(0, 4))
            },
          }}
        />
      </FormElement>
      <Button
        disabled={inputCode.length < 4}
        theme="pink"
        type="submit"
        onClick={() => void submitVerifyCode()}
      >
        Confirmar código de login
      </Button>
    </form>
  )
}

export default Login
